import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, Trash2, ArrowLeft, ArrowRight, Package, Minus, Plus } from "lucide-react";

function resolveImg(url) {
  if (!url) return "";
  return url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
}

export default function CartPage() {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);

  useEffect(() => {
    const items = JSON.parse(sessionStorage.getItem("cart_items") || "[]");
    setCartItems(items);
  }, []);

  const updateCart = (items) => {
    setCartItems(items);
    sessionStorage.setItem("cart_items", JSON.stringify(items));
  };

  const removeItem = (idx) => {
    const next = cartItems.filter((_, i) => i !== idx);
    updateCart(next);
  };

  const updateQuantity = (idx, delta) => {
    const next = [...cartItems];
    next[idx] = { ...next[idx], quantity: Math.max(1, (next[idx].quantity || 1) + delta) };
    updateCart(next);
  };

  const getItemTotal = (item) => {
    const base = item.product_price || 0;
    const frontEng = (item.front_engravings || []).reduce((s, e) => s + e.price, 0);
    const backEng = (item.back_engravings || []).reduce((s, e) => s + e.price, 0);
    return (base + frontEng + backEng) * (item.quantity || 1);
  };

  const grandTotal = cartItems.reduce((sum, item) => sum + getItemTotal(item), 0);

  const handleProceed = () => {
    if (cartItems.length === 0) return;
    sessionStorage.setItem("cart_items", JSON.stringify(cartItems));
    sessionStorage.setItem("checkout_mode", "cart");
    navigate("/shipping");
  };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="cart-page">
      <div className="border-b-2 border-black bg-white px-6 py-4 flex items-center justify-between">
        <button onClick={() => navigate("/")} className="flex items-center gap-2 font-heading font-bold text-sm uppercase tracking-wider hover:text-[#FF3366] transition-colors" data-testid="back-btn">
          <ArrowLeft size={18} strokeWidth={2.5} /> Continue Shopping
        </button>
        <span className="font-heading font-bold text-sm uppercase tracking-wider flex items-center gap-2">
          <ShoppingCart size={18} strokeWidth={2.5} /> Your Cart ({cartItems.length})
        </span>
      </div>

      <div className="max-w-4xl mx-auto p-8">
        {cartItems.length === 0 ? (
          <div className="brutal-card p-16 text-center" data-testid="empty-cart">
            <ShoppingCart size={48} className="mx-auto mb-4 text-zinc-300" strokeWidth={2} />
            <h2 className="font-heading text-xl font-bold uppercase mb-2">Your Cart is Empty</h2>
            <p className="text-zinc-500 text-sm mb-6">Add products from our catalog to get started.</p>
            <button onClick={() => navigate("/")} className="brutal-btn text-sm" data-testid="shop-btn">
              <Package size={14} className="inline mr-2" strokeWidth={2.5} /> Browse Products
            </button>
          </div>
        ) : (
          <>
            <div className="space-y-4 mb-8">
              {cartItems.map((item, idx) => (
                <div key={item.id || idx} className="brutal-card overflow-hidden" data-testid={`cart-item-${idx}`}>
                  <div className="flex gap-4 p-5">
                    {/* Product Image */}
                    <div className="w-20 h-20 border-2 border-black bg-zinc-100 flex-shrink-0 overflow-hidden">
                      {item.product_image && (
                        <img src={resolveImg(item.product_image)} alt={item.product_name} className="w-full h-full object-contain" />
                      )}
                    </div>
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading font-bold text-sm uppercase truncate">{item.product_name}</h3>
                      <p className="text-xs text-zinc-500 mt-0.5">
                        ${(item.product_price || 0).toFixed(2)} base
                        {(item.front_engravings || []).length > 0 && (
                          <> + {(item.front_engravings || []).map(e => e.name).join(", ")}</>
                        )}
                      </p>
                      {item.customization_text && (
                        <p className="text-xs text-zinc-400 mt-1 truncate">Text: &ldquo;{item.customization_text}&rdquo;</p>
                      )}
                      {item.customization_font && (
                        <p className="text-[10px] text-zinc-400">Font: {item.customization_font}</p>
                      )}
                      {item.design_description && (
                        <p className="text-[10px] text-zinc-400 truncate">Notes: {item.design_description}</p>
                      )}
                      {item.cutout_shape_name && (
                        <p className="text-xs text-zinc-400 mt-0.5">Cutout: {item.cutout_shape_name}</p>
                      )}
                      {/* Quantity */}
                      <div className="flex items-center gap-3 mt-3">
                        <button onClick={() => updateQuantity(idx, -1)} className="w-7 h-7 border-2 border-black flex items-center justify-center bg-white hover:bg-zinc-100 font-bold text-sm" data-testid={`qty-dec-${idx}`}>
                          <Minus size={12} strokeWidth={3} />
                        </button>
                        <span className="font-heading font-black text-sm w-6 text-center" data-testid={`qty-val-${idx}`}>{item.quantity || 1}</span>
                        <button onClick={() => updateQuantity(idx, 1)} className="w-7 h-7 border-2 border-black flex items-center justify-center bg-white hover:bg-zinc-100 font-bold text-sm" data-testid={`qty-inc-${idx}`}>
                          <Plus size={12} strokeWidth={3} />
                        </button>
                      </div>
                    </div>
                    {/* Price & Remove */}
                    <div className="text-right flex flex-col justify-between items-end">
                      <span className="font-heading font-black text-lg text-[#FF3366]" data-testid={`item-total-${idx}`}>${getItemTotal(item).toFixed(2)}</span>
                      <button onClick={() => removeItem(idx)} className="text-red-500 hover:text-red-700 p-1" data-testid={`remove-item-${idx}`}>
                        <Trash2 size={16} strokeWidth={2.5} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Cart Summary */}
            <div className="brutal-card p-6" data-testid="cart-summary">
              <div className="space-y-3">
                {cartItems.map((item, idx) => (
                  <div key={item.id || idx} className="flex justify-between text-sm">
                    <span className="truncate max-w-[60%]">{item.product_name} x{item.quantity || 1}</span>
                    <span className="font-bold">${getItemTotal(item).toFixed(2)}</span>
                  </div>
                ))}
                <div className="border-t-2 border-black pt-3 flex justify-between font-heading font-black text-lg">
                  <span>Cart Total</span>
                  <span className="text-[#FF3366]" data-testid="grand-total">${grandTotal.toFixed(2)}</span>
                </div>
                <p className="text-[10px] text-zinc-400">Promo codes can be applied at checkout. Promo applies to 1 item only.</p>
              </div>
              <div className="flex items-center justify-between gap-4 mt-6">
                <button onClick={() => navigate("/")} className="brutal-btn brutal-btn-outline text-sm" data-testid="continue-shopping">
                  <ArrowLeft size={14} className="inline mr-2" strokeWidth={2.5} /> Continue Shopping
                </button>
                <button onClick={handleProceed} className="brutal-btn text-sm flex items-center gap-2" data-testid="proceed-to-shipping-btn">
                  Continue to Shipping <ArrowRight size={14} strokeWidth={2.5} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
